import React from 'react';
import { Building2, GraduationCap, Users, ChevronDown, Edit3, Save, X, FileText, Loader } from 'lucide-react';

/**
 * EstablishmentGroup — one expandable group card showing all cohorts
 * (= master licenses) attached to the same establishment. Extracted from
 * EstablishmentsTab.jsx (04/06/2026 refactor) to keep that file under
 * ~200 lines and easier to read.
 *
 * Pure presentation : every state mutation goes back to the parent via
 * the callback props (onToggle, onStartEdit, onSaveEdit, onCancelEdit,
 * onChangeEditValue, onExportCohort).
 */
export default function EstablishmentGroup({
  groupKey,
  name,
  cohorts,
  masters,
  learnerCount,
  isOpen,
  editingMasterId,
  editValue,
  exportingCohortId,
  onToggle,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onChangeEditValue,
  onExportCohort,
}) {
  const isNone = groupKey === '__none__';

  return (
    <div
      className={`rounded-xl border overflow-hidden ${
        isNone ? 'bg-ink-800 border-amber-500/30' : 'bg-ink-800 border-emerald-500/30'
      }`}
      data-testid={`establishment-${groupKey}`}
    >
      <button
        onClick={() => onToggle(groupKey)}
        className={`w-full px-5 py-4 flex items-center justify-between gap-3 transition-colors hover:bg-ink-700/30 ${
          isNone ? 'bg-amber-500/5' : 'bg-emerald-500/5'
        }`}
        data-testid={`establishment-toggle-${groupKey}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
            isNone ? 'bg-amber-500/15 border border-amber-400/30' : 'bg-emerald-500/15 border border-emerald-400/30'
          }`}>
            <Building2 className={`w-5 h-5 ${isNone ? 'text-amber-300' : 'text-emerald-300'}`} />
          </div>
          <div className="text-left min-w-0">
            <p className={`font-bold ${isNone ? 'text-amber-300 italic' : 'text-slate-100'}`}>
              {isNone ? 'Sans établissement' : name}
            </p>
            <p className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">
              {masters.length} formateur·s · {learnerCount} apprenant·s
            </p>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="border-t border-ink-600 divide-y divide-ink-600">
          {cohorts.map(({ master, children }) => (
            <CohortRow
              key={master.id}
              master={master}
              children={children}
              isEditing={editingMasterId === master.id}
              editValue={editValue}
              isExporting={exportingCohortId === master.id}
              onStartEdit={onStartEdit}
              onSaveEdit={onSaveEdit}
              onCancelEdit={onCancelEdit}
              onChangeEditValue={onChangeEditValue}
              onExportCohort={onExportCohort}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CohortRow({
  master,
  children,
  isEditing,
  editValue,
  isExporting,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onChangeEditValue,
  onExportCohort,
}) {
  return (
    <div className="p-4" data-testid={`establishment-cohort-${master.id}`}>
      {/* Master row with inline edit of establishment */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <GraduationCap className="w-4 h-4 text-purple-300 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-100 truncate">{master.holder_name}</p>
            <p className="text-[10px] text-slate-500 font-mono">{master.license_key}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isEditing ? (
            <div className="flex items-center gap-1">
              <input
                value={editValue}
                onChange={(e) => onChangeEditValue(e.target.value)}
                placeholder="Établissement"
                autoFocus
                className="bg-background text-slate-100 placeholder-slate-600 border border-emerald-400/60 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-400 w-48"
                data-testid={`establishment-input-${master.id}`}
              />
              <button onClick={() => onSaveEdit(master)} className="p-1 text-money hover:bg-money/10 rounded" data-testid={`establishment-save-${master.id}`}>
                <Save className="w-3.5 h-3.5" />
              </button>
              <button onClick={onCancelEdit} className="p-1 text-slate-500 hover:bg-ink-700 rounded">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => onStartEdit(master)}
              className="text-[10px] px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 border border-emerald-500/30 flex items-center gap-1 transition-colors"
              data-testid={`establishment-edit-${master.id}`}
            >
              <Edit3 className="w-3 h-3" />
              {master.establishment ? 'Modifier' : 'Définir établissement'}
            </button>
          )}
          {children.length > 0 && (
            <button
              type="button"
              onClick={() => onExportCohort(master, children)}
              disabled={isExporting}
              className="text-[10px] px-2 py-1 rounded bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 border border-blue-500/30 flex items-center gap-1 transition-colors disabled:opacity-60 disabled:cursor-wait"
              title="Générer un PDF récapitulatif pour tous les apprenants de cette cohorte"
              data-testid={`cohort-pdf-${master.id}`}
            >
              {isExporting ? (
                <>
                  <Loader className="w-3 h-3 animate-spin" /> Génération…
                </>
              ) : (
                <>
                  <FileText className="w-3 h-3" /> Exporter PDF promo
                </>
              )}
            </button>
          )}
          <span className="text-[10px] text-slate-500 font-mono">{children.length} apprenant·s</span>
        </div>
      </div>

      {children.length === 0 ? (
        <p className="text-xs text-slate-500 italic pl-6">Aucun apprenant rattaché à cette cohorte.</p>
      ) : (
        <div className="pl-6 flex flex-wrap gap-1.5">
          {children.map(c => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1 text-xs bg-ink-700/50 border border-ink-600 rounded-md px-2 py-1 text-slate-300"
              data-testid={`establishment-learner-${c.id}`}
            >
              <Users className="w-2.5 h-2.5 text-slate-500" />
              {c.holder_name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
